// src/components/workspace/costing/WrapRateCalculator.tsx
// Interactive wrap rate calculator with comparison tools

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calculator, ArrowLeft, DollarSign, Percent, TrendingUp,
  TrendingDown, AlertCircle, CheckCircle, Sparkles, Info,
  BarChart3, Loader2, Target, Zap, RefreshCw
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { formatCurrency } from '../../../lib/pricing/utils';
import toast from 'react-hot-toast';

interface IndirectRates {
  fringe_rate: number;
  overhead_rate: number;
  ga_rate: number;
  profit_rate: number;
}

interface LaborCategory {
  id: string;
  title: string;
  base_hourly_rate: number;
  gsa_rate: number | null;
}

interface ComparisonScenario {
  name: string;
  fringe: number;
  overhead: number;
  gAndA: number;
  profit: number;
}

const DEFAULT_RATES: IndirectRates = {
  fringe_rate: 32,
  overhead_rate: 45,
  ga_rate: 12,
  profit_rate: 10
};

const INDUSTRY_SCENARIOS: ComparisonScenario[] = [
  { name: 'Small Business IT', fringe: 32, overhead: 40, gAndA: 12, profit: 10 },
  { name: 'Large IT Contractor', fringe: 38, overhead: 75, gAndA: 15, profit: 10 },
  { name: 'Professional Services', fringe: 32, overhead: 45, gAndA: 12, profit: 10 },
  { name: 'Maintenance/Facilities', fringe: 40, overhead: 30, gAndA: 10, profit: 8 },
  { name: 'Security Services', fringe: 45, overhead: 32, gAndA: 10, profit: 8 },
  { name: 'Food Services', fringe: 42, overhead: 28, gAndA: 8, profit: 6 },
  { name: 'Transportation', fringe: 40, overhead: 30, gAndA: 8, profit: 8 },
  { name: 'Healthcare', fringe: 38, overhead: 45, gAndA: 12, profit: 10 },
];

export function WrapRateCalculator({ embedded = false }: { embedded?: boolean } = {}) {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rates, setRates] = useState<IndirectRates>(DEFAULT_RATES);
  const [laborCategories, setLaborCategories] = useState<LaborCategory[]>([]);
  const [selectedBaseRate, setSelectedBaseRate] = useState<number>(100);
  const [showComparison, setShowComparison] = useState(false);
  const [customScenario, setCustomScenario] = useState<ComparisonScenario | null>(null);

  useEffect(() => {
    if (profile) {
      loadData();
    }
  }, [profile]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load indirect rates
      const { data: ratesData, error: ratesError } = await supabase
        .from('company_indirect_rates')
        .select('fringe_rate, overhead_rate, ga_rate, profit_rate')
        .eq('profile_id', profile?.id)
        .single();

      if (ratesError && ratesError.code !== 'PGRST116') {
        console.error('Error loading indirect rates:', ratesError);
      }

      // Load labor categories
      const { data: laborData, error: laborError } = await supabase
        .from('company_labor_categories')
        .select('id, title, base_hourly_rate, gsa_rate')
        .eq('profile_id', profile?.id)
        .order('base_hourly_rate', { ascending: false })
        .limit(10);

      if (laborError && laborError.code !== 'PGRST116') {
        console.error('Error loading labor categories:', laborError);
      }

      if (ratesData) {
        setRates(ratesData);
      }
      setLaborCategories(laborData || []);

    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate wrap rate using compounded method (industry standard)
  const calculateWrapRate = (r: IndirectRates): number => {
    return (1 + r.fringe_rate / 100) *
           (1 + r.overhead_rate / 100) *
           (1 + r.ga_rate / 100) *
           (1 + r.profit_rate / 100);
  };

  // Calculate wrap rate for a scenario
  const calculateScenarioWrapRate = (s: ComparisonScenario): number => {
    return (1 + s.fringe / 100) *
           (1 + s.overhead / 100) *
           (1 + s.gAndA / 100) *
           (1 + s.profit / 100);
  };

  const wrapRate = calculateWrapRate(rates);
  const loadedRate = selectedBaseRate * wrapRate;

  // Break down the cost buildup
  const costBreakdown = {
    base: selectedBaseRate,
    withFringe: selectedBaseRate * (1 + rates.fringe_rate / 100),
    withOverhead: selectedBaseRate * (1 + rates.fringe_rate / 100) * (1 + rates.overhead_rate / 100),
    withGA: selectedBaseRate * (1 + rates.fringe_rate / 100) * (1 + rates.overhead_rate / 100) * (1 + rates.ga_rate / 100),
    withProfit: loadedRate
  };

  const getCompetitivenessRating = (rate: number): { label: string; color: string; message: string } => {
    if (rate < 1.5) {
      return {
        label: 'Very Low',
        color: 'red',
        message: 'Your rates may not cover actual costs. Verify all indirect costs are captured.'
      };
    } else if (rate < 1.8) {
      return {
        label: 'Low',
        color: 'yellow',
        message: 'Below typical federal rates. Competitive for cost-sensitive bids.'
      };
    } else if (rate <= 2.3) {
      return {
        label: 'Competitive',
        color: 'green',
        message: 'Within the typical range for federal contracts (1.8x - 2.3x).'
      };
    } else if (rate <= 2.8) {
      return {
        label: 'Above Average',
        color: 'yellow',
        message: 'Higher than typical. May need strong technical differentiators to win.'
      };
    } else {
      return {
        label: 'High',
        color: 'red',
        message: 'Significantly above typical rates. Consider reviewing indirect cost structure.'
      };
    }
  };

  const competitiveness = getCompetitivenessRating(wrapRate);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-bw-primary" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'max-w-5xl mx-auto p-6'}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          {!embedded && (
          <button
            onClick={() => navigate('/workspace/costing')}
            className="flex items-center text-sm text-bw-muted hover:text-bw-ink mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Costing Studio
          </button>
          )}
          <h1 className="text-2xl font-bold text-bw-ink flex items-center">
            <BarChart3 className="h-6 w-6 mr-2 text-bw-primary" />
            Wrap Rate Calculator
          </h1>
          <p className="text-bw-muted mt-1">
            Analyze your loaded rates and compare with industry benchmarks
          </p>
        </div>
        <button
          onClick={() => navigate('/workspace/costing/indirect-rates')}
          className="flex items-center px-4 py-2 text-bw-primary border border-servet-blue rounded-lg hover:bg-bw-primary-tint transition-colors"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Edit Rates
        </button>
      </div>

      {/* Main Calculator Card */}
      <div className={`rounded-xl p-6 mb-6 ${
        competitiveness.color === 'green' ? 'bg-bw-surface-subtle border border-green-200' :
        competitiveness.color === 'yellow' ? 'bg-bw-surface-subtle border border-yellow-200' :
        'bg-bw-surface-subtle border border-red-200'
      }`}>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center">
              {competitiveness.color === 'green' ? (
                <CheckCircle className="h-6 w-6 text-success mr-2" />
              ) : (
                <AlertCircle className={`h-6 w-6 mr-2 ${
                  competitiveness.color === 'yellow' ? 'text-fit-moderate' : 'text-urgent'
                }`} />
              )}
              <h2 className="text-lg font-semibold text-bw-ink">Your Wrap Rate</h2>
              <span className={`ml-3 px-2 py-0.5 text-xs rounded-full ${
                competitiveness.color === 'green' ? 'bg-success-bg text-success' :
                competitiveness.color === 'yellow' ? 'bg-fit-moderate-bg text-fit-moderate' :
                'bg-urgent-bg text-urgent'
              }`}>
                {competitiveness.label}
              </span>
            </div>
            <p className={`text-sm mt-1 ${
              competitiveness.color === 'green' ? 'text-success' :
              competitiveness.color === 'yellow' ? 'text-fit-moderate' :
              'text-urgent'
            }`}>
              {competitiveness.message}
            </p>
          </div>
          <div className="text-right">
            <div className="text-5xl font-bold text-bw-ink">{wrapRate.toFixed(3)}x</div>
            <div className="text-sm text-bw-muted">Multiplier</div>
          </div>
        </div>

        {/* Interactive Base Rate Calculator */}
        <div className="mt-6 p-4 bg-white/50 rounded-lg">
          <label className="block text-sm font-medium text-bw-ink mb-2">
            Calculate loaded rate for base rate:
          </label>
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-bw-muted" />
              <input
                type="number"
                value={selectedBaseRate}
                onChange={(e) => setSelectedBaseRate(parseFloat(e.target.value) || 0)}
                className="w-full pl-10 pr-4 py-3 text-xl font-semibold border border-bw-border rounded-lg focus:ring-2 focus:ring-bw-primary focus:border-transparent"
              />
              <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-bw-muted">/hr base</span>
            </div>
            <div className="text-2xl text-bw-muted">=</div>
            <div className="flex-1 py-3 px-4 bg-bw-surface rounded-lg border border-bw-border">
              <div className="text-xl font-bold text-success">${loadedRate.toFixed(2)}/hr</div>
              <div className="text-xs text-bw-muted">Fully Loaded</div>
            </div>
          </div>
        </div>

        {/* Cost Buildup Visualization */}
        <div className="mt-6">
          <h4 className="text-sm font-medium text-bw-ink mb-3">Cost Buildup</h4>
          <div className="flex items-center h-12 rounded-lg overflow-hidden">
            <div
              className="h-full bg-bw-primary-tint0 flex items-center justify-center text-white text-xs font-medium transition-all"
              style={{ width: `${(costBreakdown.base / costBreakdown.withProfit) * 100}%` }}
            >
              Base ${costBreakdown.base.toFixed(0)}
            </div>
            <div
              className="h-full bg-success-bg0 flex items-center justify-center text-white text-xs font-medium transition-all"
              style={{ width: `${((costBreakdown.withFringe - costBreakdown.base) / costBreakdown.withProfit) * 100}%` }}
            >
              +${(costBreakdown.withFringe - costBreakdown.base).toFixed(0)}
            </div>
            <div
              className="h-full bg-fit-moderate-bg0 flex items-center justify-center text-white text-xs font-medium transition-all"
              style={{ width: `${((costBreakdown.withOverhead - costBreakdown.withFringe) / costBreakdown.withProfit) * 100}%` }}
            >
              +${(costBreakdown.withOverhead - costBreakdown.withFringe).toFixed(0)}
            </div>
            <div
              className="h-full bg-orange-500 flex items-center justify-center text-white text-xs font-medium transition-all"
              style={{ width: `${((costBreakdown.withGA - costBreakdown.withOverhead) / costBreakdown.withProfit) * 100}%` }}
            >
              +${(costBreakdown.withGA - costBreakdown.withOverhead).toFixed(0)}
            </div>
            <div
              className="h-full bg-bw-primary-tint0 flex items-center justify-center text-white text-xs font-medium transition-all"
              style={{ width: `${((costBreakdown.withProfit - costBreakdown.withGA) / costBreakdown.withProfit) * 100}%` }}
            >
              +${(costBreakdown.withProfit - costBreakdown.withGA).toFixed(0)}
            </div>
          </div>
          <div className="flex justify-between text-xs text-bw-muted mt-2">
            <span>Base</span>
            <span>Fringe ({rates.fringe_rate}%)</span>
            <span>Overhead ({rates.overhead_rate}%)</span>
            <span>G&A ({rates.ga_rate}%)</span>
            <span>Profit ({rates.profit_rate}%)</span>
          </div>
        </div>
      </div>

      {/* Your Rates Summary */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-bw-surface rounded-xl border border-bw-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-bw-muted">Fringe</span>
            <span className="text-xs px-1.5 py-0.5 bg-success-bg text-success rounded">Benefits</span>
          </div>
          <div className="text-2xl font-bold text-bw-ink">{rates.fringe_rate}%</div>
          <div className="text-xs text-bw-muted mt-1">Industry: 28-45%</div>
        </div>
        <div className="bg-bw-surface rounded-xl border border-bw-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-bw-muted">Overhead</span>
            <span className="text-xs px-1.5 py-0.5 bg-fit-moderate-bg text-fit-moderate rounded">Facilities</span>
          </div>
          <div className="text-2xl font-bold text-bw-ink">{rates.overhead_rate}%</div>
          <div className="text-xs text-bw-muted mt-1">Industry: 25-120%</div>
        </div>
        <div className="bg-bw-surface rounded-xl border border-bw-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-bw-muted">G&A</span>
            <span className="text-xs px-1.5 py-0.5 bg-orange-100 text-fit-moderate rounded">Admin</span>
          </div>
          <div className="text-2xl font-bold text-bw-ink">{rates.ga_rate}%</div>
          <div className="text-xs text-bw-muted mt-1">Industry: 8-25%</div>
        </div>
        <div className="bg-bw-surface rounded-xl border border-bw-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-bw-muted">Profit</span>
            <span className="text-xs px-1.5 py-0.5 bg-bw-primary-tint text-bw-primary rounded">Fee</span>
          </div>
          <div className="text-2xl font-bold text-bw-ink">{rates.profit_rate}%</div>
          <div className="text-xs text-bw-muted mt-1">FAR Limit: 6-15%</div>
        </div>
      </div>

      {/* Labor Categories with Loaded Rates */}
      {laborCategories.length > 0 && (
        <div className="bg-bw-surface rounded-xl border border-bw-border p-5 mb-6">
          <h3 className="font-semibold text-bw-ink mb-4">Your Labor Categories (Loaded Rates)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 text-bw-muted font-medium">Category</th>
                  <th className="text-right py-2 text-bw-muted font-medium">Base Rate</th>
                  <th className="text-right py-2 text-bw-muted font-medium">Loaded Rate</th>
                  <th className="text-right py-2 text-bw-muted font-medium">GSA Rate</th>
                  <th className="text-right py-2 text-bw-muted font-medium">vs GSA</th>
                </tr>
              </thead>
              <tbody>
                {laborCategories.map((cat) => {
                  const catLoadedRate = cat.base_hourly_rate * wrapRate;
                  const gsaDiff = cat.gsa_rate ? ((catLoadedRate - cat.gsa_rate) / cat.gsa_rate) * 100 : null;

                  return (
                    <tr key={cat.id} className="border-b last:border-b-0">
                      <td className="py-3 font-medium">{cat.title}</td>
                      <td className="py-3 text-right">${cat.base_hourly_rate.toFixed(2)}/hr</td>
                      <td className="py-3 text-right font-semibold text-success">
                        ${catLoadedRate.toFixed(2)}/hr
                      </td>
                      <td className="py-3 text-right text-bw-muted">
                        {cat.gsa_rate ? `$${cat.gsa_rate.toFixed(2)}/hr` : '-'}
                      </td>
                      <td className="py-3 text-right">
                        {gsaDiff !== null && (
                          <span className={`flex items-center justify-end ${
                            gsaDiff < -5 ? 'text-success' :
                            gsaDiff > 10 ? 'text-urgent' :
                            'text-bw-muted'
                          }`}>
                            {gsaDiff > 0 ? (
                              <TrendingUp className="h-3 w-3 mr-1" />
                            ) : (
                              <TrendingDown className="h-3 w-3 mr-1" />
                            )}
                            {gsaDiff > 0 ? '+' : ''}{gsaDiff.toFixed(1)}%
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Industry Comparison */}
      <div className="bg-bw-surface rounded-xl border border-bw-border p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Target className="h-5 w-5 text-bw-primary mr-2" />
            <h3 className="font-semibold text-bw-ink">Industry Comparison</h3>
          </div>
          <button
            onClick={() => setShowComparison(!showComparison)}
            className="text-sm text-bw-primary hover:text-bw-primary"
          >
            {showComparison ? 'Hide Details' : 'Show Details'}
          </button>
        </div>

        <div className="grid grid-cols-4 gap-3">
          {INDUSTRY_SCENARIOS.map((scenario, index) => {
            const scenarioRate = calculateScenarioWrapRate(scenario);
            const diff = ((wrapRate - scenarioRate) / scenarioRate) * 100;

            return (
              <div
                key={index}
                className={`p-3 rounded-lg border ${
                  Math.abs(diff) < 5 ? 'border-green-200 bg-success-bg' :
                  diff > 0 ? 'border-red-200 bg-urgent-bg' :
                  'border-blue-200 bg-bw-primary-tint'
                }`}
              >
                <div className="text-sm font-medium text-bw-ink">{scenario.name}</div>
                <div className="text-lg font-bold text-bw-ink mt-1">{scenarioRate.toFixed(2)}x</div>
                <div className={`text-xs mt-1 flex items-center ${
                  Math.abs(diff) < 5 ? 'text-success' :
                  diff > 0 ? 'text-urgent' :
                  'text-bw-primary'
                }`}>
                  {Math.abs(diff) < 5 ? (
                    <>
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Similar
                    </>
                  ) : diff > 0 ? (
                    <>
                      <TrendingUp className="h-3 w-3 mr-1" />
                      You're {diff.toFixed(0)}% higher
                    </>
                  ) : (
                    <>
                      <TrendingDown className="h-3 w-3 mr-1" />
                      You're {Math.abs(diff).toFixed(0)}% lower
                    </>
                  )}
                </div>

                {showComparison && (
                  <div className="mt-2 pt-2 border-t border-bw-border text-xs text-bw-muted">
                    <div>F: {scenario.fringe}% | OH: {scenario.overhead}%</div>
                    <div>G&A: {scenario.gAndA}% | Profit: {scenario.profit}%</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Calculation Explanation */}
      <div className="bg-bw-primary-tint rounded-xl border border-blue-200 p-5">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-bw-primary mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-bw-primary">Understanding Wrap Rates</h4>
            <div className="text-sm text-bw-primary mt-2 space-y-2">
              <p>
                <strong>Compounded Method (Used Here):</strong><br />
                Wrap Rate = (1 + Fringe%) × (1 + Overhead%) × (1 + G&A%) × (1 + Profit%)<br />
                = (1 + {rates.fringe_rate/100}) × (1 + {rates.overhead_rate/100}) × (1 + {rates.ga_rate/100}) × (1 + {rates.profit_rate/100})<br />
                = <strong>{wrapRate.toFixed(4)}x</strong>
              </p>
              <p className="text-xs text-bw-primary pt-2 border-t border-blue-200">
                This compounding method is standard for federal government contracting. Each rate is applied
                sequentially, meaning overhead is calculated on the base plus fringe, G&A is calculated on
                the subtotal, etc. This is the method required by DCAA for auditable indirect rate structures.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
